/**
 * Common Component Patterns
 * Pre-built component configurations following best practices
 */

import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Common gradient backgrounds
 */
export const gradients = {
  // Company header gradient - subtle warm
  companyHeader: 'linear-gradient(135deg, #faf8f4 0%, #f2ede4 75%, #faf8f4 100%)',

  // Primary brand gradient (terracotta)
  primary: 'linear-gradient(135deg, #c05a3a 0%, #d4825e 100%)',

  // Review banner gradient - warm tones
  reviewBanner: 'linear-gradient(135deg, #faf8f4 0%, #ebe4d8 75%, #faf8f4 100%)',

  // Subtle background gradient
  subtle: 'linear-gradient(180deg, #ffffff 0%, #f2ede4 100%)',

  // Success gradient (sage)
  success: 'linear-gradient(90deg, #3d6149 0%, #4e7c5f 100%)',

  // Warning gradient
  warning: 'linear-gradient(90deg, #ffc01e 0%, #D4AF37 100%)',
};

// Common button constants
const BUTTON_HOVER_TRANSFORM = 'translateY(-1px)';
const BUTTON_FAST_TRANSITION = 'all 0.2s ease';
const BUTTON_MIN_HEIGHT = 40;

/**
 * Common button configurations
 */
export const commonButtons = {
  // Large call-to-action button
  cta: {
    '&:hover': {
      boxShadow: '0 4px 16px rgba(42, 32, 24, 0.15)',
      transform: 'translateY(-2px)',
    },
    borderRadius: 2,
    fontSize: '1rem',
    fontWeight: 600,
    minWidth: 140,
    padding: '10px 24px',
    transition: 'all 0.3s ease',
  } as SxProps<Theme>,

  // Destructive action button
  destructive: {
    '&:hover': {
      bgcolor: 'error.dark',
      boxShadow: '0 4px 12px rgba(218, 7, 28, 0.3)',
      transform: BUTTON_HOVER_TRANSFORM,
    },
    '&.Mui-disabled': {
      bgcolor: '#d8cfc0',
      color: 'rgba(255, 255, 255, 0.7)',
    },
    bgcolor: 'error.main',
    borderRadius: 2,
    color: 'white',
    fontWeight: 600,
    minHeight: BUTTON_MIN_HEIGHT,
    px: 3,
    transition: BUTTON_FAST_TRANSITION,
  } as SxProps<Theme>,

  // Icon button with circular shape
  iconCircular: {
    '&:hover': {
      bgcolor: 'primary.light',
    },
    bgcolor: 'primary.main',
    borderRadius: '50%',
    color: 'white',
    height: 48,
    width: 48,
  } as SxProps<Theme>,

  // Outlined button with hover effect (legacy)
  outlinedPrimary: {
    '&:hover': {
      bgcolor: 'primary.light',
      borderColor: 'primary.dark',
      color: 'primary.dark',
    },
    borderColor: 'primary.main',
    borderWidth: 2,
    color: 'primary.main',
  } as SxProps<Theme>,

  // Primary action button
  primary: {
    '&:hover': {
      background: gradients.primary,
      boxShadow: '0 4px 12px rgba(192, 90, 58, 0.3)',
      transform: BUTTON_HOVER_TRANSFORM,
    },
    '&.Mui-disabled': {
      background: '#d8cfc0',
      color: 'rgba(255, 255, 255, 0.7)',
    },
    background: gradients.primary,
    borderRadius: 2,
    color: 'white',
    fontWeight: 600,
    minHeight: BUTTON_MIN_HEIGHT,
    px: 3,
    transition: BUTTON_FAST_TRANSITION,
  } as SxProps<Theme>,

  // Secondary action button
  secondary: {
    '&:hover': {
      bgcolor: 'primary.light',
      borderColor: 'primary.main',
      boxShadow: '0 2px 8px rgba(192, 90, 58, 0.2)',
      color: 'primary.dark',
      transform: BUTTON_HOVER_TRANSFORM,
    },
    '&.Mui-disabled': {
      borderColor: '#d8cfc0',
      color: '#b8ad9e',
    },
    bgcolor: 'transparent',
    border: '2px solid',
    borderColor: 'primary.light',
    borderRadius: 2,
    color: 'primary.main',
    fontWeight: 600,
    minHeight: BUTTON_MIN_HEIGHT,
    px: 3,
    transition: BUTTON_FAST_TRANSITION,
  } as SxProps<Theme>,
};

/**
 * Common card configurations
 */
export const commonCards = {
  // Bordered card
  bordered: {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    p: 3,
  } as SxProps<Theme>,

  // Elevated card with hover effect
  elevated: {
    '&:hover': {
      boxShadow: '0 8px 24px rgba(42, 32, 24, 0.1)',
      transform: 'translateY(-4px)',
    },
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 3,
    boxShadow: '0 2px 12px rgba(42, 32, 24, 0.06)',
    p: 3,
    transition: 'all 0.3s ease',
  } as SxProps<Theme>,

  // Feature card with gradient border
  feature: {
    '&::before': {
      background: gradients.primary,
      borderRadius: 3,
      content: '""',
      inset: 0,
      opacity: 0,
      padding: 2,
      position: 'absolute',
      transition: 'opacity 0.3s ease',
      zIndex: -1,
    },
    '&:hover::before': {
      opacity: 1,
    },
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: '0 2px 12px rgba(42, 32, 24, 0.06)',
    p: 3,
    position: 'relative',
    transition: 'all 0.3s ease',
  } as SxProps<Theme>,
};

/**
 * Common typography configurations
 */
export const commonTypography = {
  // Caption with icon
  captionWithIcon: {
    alignItems: 'center',
    color: 'text.secondary',
    display: 'flex',
    fontSize: '0.875rem',
    gap: 0.5,
  } as SxProps<Theme>,

  // Gradient text
  gradient: {
    background: gradients.primary,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  } as SxProps<Theme>,

  // Heading with bottom border
  headingBordered: {
    '&::after': {
      bgcolor: 'primary.main',
      content: '""',
      display: 'block',
      height: 4,
      mt: 1,
      width: 60,
    },
    fontWeight: 700,
    mb: 2,
  } as SxProps<Theme>,
};

/**
 * Common container configurations
 */
export const commonContainers = {
  // Hero section
  hero: {
    alignItems: 'center',
    background: gradients.primary,
    color: 'white',
    display: 'flex',
    minHeight: {
      md: 500,
      xs: 400,
    },
    px: {
      lg: 8,
      md: 6,
      xs: 3,
    },
    textAlign: 'center',
  } as SxProps<Theme>,

  // Centered page container
  page: {
    margin: '0 auto',
    maxWidth: 'lg',
    px: {
      lg: 8,
      md: 6,
      sm: 4,
      xs: 3,
    },
    py: {
      lg: 4,
      md: 2,
      xs: 1,
    },
  } as SxProps<Theme>,

  // Section container with padding
  section: {
    py: {
      md: 8,
      xs: 6,
    },
  } as SxProps<Theme>,
};

/**
 * Common form field configurations
 */
export const commonFormFields = {
  // Large search field
  search: {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'background.paper',
      borderRadius: 4,
      fontSize: '1.125rem',
    },
    width: '100%',
  } as SxProps<Theme>,

  // Standard text field
  standard: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: 'primary.light',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
        borderWidth: 2,
      },
      borderRadius: 1,
    },
  } as SxProps<Theme>,
};

/**
 * Loading states
 */
export const loadingStates = {
  // Skeleton loading
  skeleton: {
    animation: 'pulse 1.5s ease-in-out infinite',
    bgcolor: 'grey.200',
    borderRadius: 1,
  } as SxProps<Theme>,

  // Spinner container
  spinnerContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 200,
  } as SxProps<Theme>,
};

/**
 * Status indicators
 */
export const statusIndicators = {
  // Badge
  badge: (color: 'error' | 'info' | 'success' | 'warning') => {
    return {
      bgcolor: `${color}.light`,
      borderRadius: 2,
      color: `${color}.dark`,
      fontSize: '0.75rem',
      fontWeight: 600,
      px: 1.5,
      py: 0.5,
    } as SxProps<Theme>;
  },

  // Dot indicator
  dot: (color: 'error' | 'info' | 'success' | 'warning') => {
    return {
      bgcolor: `${color}.main`,
      borderRadius: '50%',
      display: 'inline-block',
      height: 8,
      mr: 1,
      width: 8,
    } as SxProps<Theme>;
  },
};

/**
 * Navigation patterns
 */
export const navigationPatterns = {
  // Navbar — warm sand background
  navbar: {
    alignItems: 'center',
    background: '#f2ede4',
    borderBottom: '1px solid #d8cfc0',
    color: '#2a2018',
    display: 'flex',
    minHeight: 80,
    px: {
      md: 4,
      xs: 3,
    },
  } as SxProps<Theme>,

  // Sidebar
  sidebar: {
    bgcolor: 'background.paper',
    borderRight: '1px solid',
    borderRightColor: 'divider',
    height: '100vh',
    overflowY: 'auto',
    p: 2,
    position: 'fixed',
    width: 280,
  } as SxProps<Theme>,

  // Tab bar
  tabBar: {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    display: 'flex',
    gap: 2,
    px: 2,
  } as SxProps<Theme>,
};

/**
 * Grid patterns
 */
export const gridPatterns = {
  // Responsive 4-column grid
  fourColumn: {
    display: 'grid',
    gap: 3,
    gridTemplateColumns: {
      lg: 'repeat(4, 1fr)',
      md: 'repeat(2, 1fr)',
      xs: '1fr',
    },
  } as SxProps<Theme>,

  // Masonry-style grid
  masonry: {
    columnCount: {
      lg: 3,
      md: 2,
      xs: 1,
    },
    columnGap: 3,
  } as SxProps<Theme>,

  // Responsive 3-column grid
  threeColumn: {
    display: 'grid',
    gap: 3,
    gridTemplateColumns: {
      lg: 'repeat(3, 1fr)',
      md: 'repeat(2, 1fr)',
      xs: '1fr',
    },
  } as SxProps<Theme>,
};

/**
 * Animation patterns
 */
export const animations = {
  // Fade in
  fadeIn: {
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animation: 'fadeIn 0.5s ease-in',
  } as SxProps<Theme>,

  // Pulse
  pulse: {
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.5 },
    },
    animation: 'pulse 1.5s ease-in-out infinite',
  } as SxProps<Theme>,

  // Slide up
  slideUp: {
    '@keyframes slideUp': {
      from: {
        opacity: 0,
        transform: 'translateY(20px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    animation: 'slideUp 0.5s ease-out',
  } as SxProps<Theme>,
};
